import React from 'react';
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import moment from 'moment';
import { Notifications } from "modules/notifications";
import ViewDetailsPopup from './ViewDetails';
import { CopyIcon } from "assets/icons";
import { useSelector, useDispatch } from 'react-redux';
import { campaignList } from 'modules/api/methods/campaign';
import offerBanner1 from 'assets/images/offerBanner1.png';
import offerBanner2 from 'assets/images/offerBanner2.png';

const MainSection = styled.div`
  margin: 50px 100px;

  @media (max-width: ${(props) =>
    props.theme.custom.breakpoints["md-breakpoint"]}) {
    margin: 20px;
  }
`;

const Headings = styled.div`
  text-transform: uppercase;
  text-align: center;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
`;

const GridSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
`;

const Slider = styled.div`
  box-shadow: 0px 12px 24px rgba(58, 58, 68, 0.08), 0px 24px 48px rgba(90, 91, 106, 0.08);
  border-radius: 8px;
  margin: 30px 15px;
  cursor: pointer;
  .slider__sec {
    padding: 20px;
  }

  .slider__title {
    font-weight: 700;
    font-size: 20px;
    line-height: 24px;
    color: 292A31;
  }

  .slider__subTitle {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #292A31;
    padding: 5px 0px;
  }

  .border {
    border-top: 1px solid #E1E1E3;
  }

  .slider__codeSec {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;

    .slider__code {
      display: flex;
      align-items: center;
      background: #787885;
      border-radius: 4px;
      padding: 5px 8px;
      color: #fff;
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;

      span {
        margin-right: 10px;
      }
    }

    .slider__details {
      font-weight: 600;
      font-size: 14px;
      line-height: 16px;
      color: #2979FF;
      text-transform: uppercase;
      text-align: center;
    }
  }

`;

const AllOffers = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("offer");
  const [showDetails, setShowDetails] = React.useState(false);
  const campaignData = useSelector((data: any) => data?.campaign?.listData);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const currency = useSelector<any>(val => val?.region?.currency);
  const [campaignListData, setCampaignData] = React.useState([]);
  const [viewInfoData, setViewInfoData] = React.useState([]);

  React.useEffect(() => {
    const getCampaign = async () => {
      const res = await campaignList();
      const tempArray: any = [];
      const popupData: any = [];
      res?.map((data: any, index: number) => {
        if (data?.status === 'Active') {
          const travelFrom = moment(data?.travelDates?.startDate).format('MMM DD');
          const travelTo = moment(data?.travelDates?.endDate).format('MMM DD');
          const couponValidFrom = moment(data?.couponValidity?.startDate).format('MMM DD');
          const couponValidTo = moment(data?.couponValidity?.endDate).format('MMM DD');
          const validTill = moment(data?.couponValidity?.endDate).format('DD MMM YYYY');
          const tempData = {
            banner: index % 2 === 0 ? offerBanner1 : offerBanner2,
            title: `${t('flat')} ${currency} ${data?.couponData?.maxDiscount} ${t('off')}`,
            subText: `${t('ifTravelling')} ${travelFrom} ${t('to')} ${travelTo}`,
            bookingText: `${t('makeBooking')} ${couponValidFrom} ${t('to')} ${couponValidTo}`,
            code: data?.coupon,
          }
          tempArray.push(tempData);

          const infoData = {
            title: `${t('flat')} ${currency} ${data?.couponData?.maxDiscount} ${t('off')}`,
            amount: `${currency} ${data?.couponData?.maxDiscount}`,
            code: data?.coupon,
            valid: validTill,
            discount: data?.couponData?.type === 'percentage' ? `${data?.couponData?.discountValue}%` : `${currency} ${data?.couponData?.discountValue}`
          }
          popupData.push(infoData);
        }
      });
      setCampaignData(tempArray);
      setViewInfoData(popupData);
      const campaignValues = {
        listData: tempArray,
        popupData: popupData
      }
      dispatch({ type: 'CAMPAIGN_LIST', payload: campaignValues })
    }

    if (campaignData) {
      setCampaignData(campaignData?.listData);
      setViewInfoData(campaignData?.popupData);
    } else {
      getCampaign();
    }
  }, [currency, t, campaignData, dispatch])

  const toggleModal = (index: any) => {
    setShowDetails(!showDetails);
    setCurrentIndex(index);
  }

  const copyCouponCode = (code: string) => {
    navigator.clipboard.writeText(code);
    Notifications.enqueueSnackbar(`${t('coupon')} '${code}' ${t('copied')}`, {
      preventDuplicate: true,
    });
  }

  return (
    <MainSection>
      <Headings>{t('offer')}</Headings>

      <GridSection>
        {campaignListData?.map((item: any, index: number) => (
          <Slider key={index}>
            <img style={{ width: '100%' }} src={item.banner} alt="" />
            <div className="slider__sec">
              <p className="slider__title">{item.title}</p>
              <p className="slider__subTitle">{item.subText}</p>
              <p className="slider__subTitle border">{item.bookingText}</p>

              <div className="slider__codeSec">
                <p className="slider__code" onClick={() => copyCouponCode(item.code)}>
                  <span>{t('useCode')} '{item.code}'</span><CopyIcon />
                </p>
                <p className="slider__details" onClick={() => toggleModal(index)}>{t('viewDetails')}</p>
              </div>
            </div>
          </Slider>
        ))}
      </GridSection>

      <ViewDetailsPopup onCloseModal={() => toggleModal(currentIndex)} isOpen={showDetails} viewInfoData={viewInfoData[currentIndex]} />
    </MainSection>
  )
}

export default AllOffers;
